import { useState } from "react";
import { X, Linkedin, Users, UserPlus, RefreshCw } from "lucide-react";
import type { Company, HiringContact } from "../api/HiringContactsApi";
import { useHiringContacts } from "../modal/HiringContactsContext";
import { WatchToggle, PriorityBadge } from "./ContactCard";
import AssignContactModal from "./AssignContactModal";

interface RowProps {
  contact: HiringContact;
}

function ContactRow({ contact }: RowProps) {
  const { handleContactWatchToggle, handleContactPriorityChange } = useHiringContacts();
  const [assignTarget, setAssignTarget] = useState<HiringContact | null>(null);

  const assignees = contact.assigned_employees ?? [];

  return (
    <div className="p-2 rounded-lg bg-surface-muted/40 border border-border space-y-1.5">
      <div className="flex items-center gap-2">
        <PriorityBadge priority={contact.priority} onChange={(p) => handleContactPriorityChange(contact.id, p)} />
        <WatchToggle isWatched={contact.is_watched} onToggle={() => handleContactWatchToggle(contact.id, !contact.is_watched)} />
        <a
          href={contact.linkedin_url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-foreground hover:underline font-inter inline-flex items-center gap-1 min-w-0 truncate"
        >
          {contact.full_name}
          <Linkedin className="w-3 h-3 text-sky-400 shrink-0" />
        </a>
      </div>

      <div className="flex items-center justify-between gap-2 pl-8">
        <span className="text-[11px] text-muted-foreground font-inter truncate">
          {contact.role ? `${contact.role} · ` : ""}
          {assignees.length > 0 ? `Assigned: ${assignees.map((a) => a.name).join(", ")}` : "Unassigned"}
        </span>
        <button
          onClick={() => setAssignTarget(contact)}
          className="shrink-0 flex items-center gap-1 text-[11px] text-primary hover:text-primary-foreground bg-primary/20 hover:bg-primary border border-primary/30 hover:border-primary rounded-lg px-2 py-1 transition-colors font-inter"
        >
          <UserPlus className="w-3 h-3" />
          Assign
        </button>
      </div>

      {assignTarget && (
        <AssignContactModal contact={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  );
}

interface Props {
  company: Company;
  onClose: () => void;
}

export default function CompanyContactsModal({ company, onClose }: Props) {
  const { expandedCompanyContacts, expandedCompanyContactsLoading } = useHiringContacts();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col p-6">
        <div className="flex items-start justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground font-inter inline-flex items-center gap-1.5 truncate">
                {company.name}
                {company.linkedin_url && (
                  <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-400 shrink-0">
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                )}
              </h2>
              <p className="text-xs text-muted-foreground font-inter mt-0.5 truncate">{company.domain}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1">
          {expandedCompanyContactsLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground font-inter text-xs">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading contacts…
            </div>
          ) : expandedCompanyContacts.length === 0 ? (
            <p className="text-xs text-muted-foreground font-inter text-center py-10">
              No hiring contacts found for this company yet.
            </p>
          ) : (
            expandedCompanyContacts.map((contact) => <ContactRow key={contact.id} contact={contact} />)
          )}
        </div>

        <div className="flex justify-end mt-4 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
